import { ReactElement } from 'react';
import { Controller, Control, FieldErrors, FieldValues, Path } from 'react-hook-form';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { MobileTimePicker as MUITimePicker } from '@mui/x-date-pickers/MobileTimePicker';
import get from 'lodash/get';
import FormControl from '@mui/material/FormControl';

import { ReactComponent as ClockIcon } from '~/ui/assets/images/clockIcon.svg';
import {
  PickerDraftActionBar,
  PickerDraftActionsContext,
  usePickerDraft,
} from '~/ui/components/inputs/PickerDraft';

import styles from './TimePicker.module.scss';

interface ITimePickerFieldProps {
  value: Date | null;
  onChange: (value: Date | null) => void;
  label?: string;
  error?: string;
  placeholder?: string;
  disabled?: boolean;
  size: 'small' | 'medium';
}

const TimePickerField = ({
  value,
  onChange,
  label,
  error,
  placeholder,
  disabled,
  size,
}: ITimePickerFieldProps): ReactElement => {
  const picker = usePickerDraft({ value, onCommit: onChange, disabled });

  return (
    <FormControl variant="outlined" size={size} fullWidth>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <PickerDraftActionsContext.Provider value={picker.actions}>
          <MUITimePicker
            {...picker.pickerProps}
            label={label}
            disabled={disabled}
            className={styles.timePickerControl}
            slots={{ openPickerIcon: ClockIcon, actionBar: PickerDraftActionBar }}
            slotProps={{
              textField: {
                variant: 'outlined',
                size,
                fullWidth: true,
                error: !!error,
                helperText: error,
                ...picker.textFieldProps,
                slotProps: {
                  htmlInput: { placeholder },
                  inputLabel: { shrink: true },
                  formHelperText: { className: styles.errorText },
                },
              },
              dialog: picker.dialogProps,
            }}
          />
        </PickerDraftActionsContext.Provider>
      </LocalizationProvider>
    </FormControl>
  );
};

interface IProps<TFieldValues extends FieldValues = FieldValues> {
  control: Control<TFieldValues>;
  errors: FieldErrors;
  name: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  size?: 'small' | 'medium';
}

const TimePicker = <TFieldValues extends FieldValues = FieldValues>({
  control,
  name,
  label,
  errors,
  placeholder,
  disabled,
  size = 'small',
}: IProps<TFieldValues>): ReactElement => {
  const error = get(errors, `${name}.message`, '');

  return (
    <Controller
      control={control}
      name={name as Path<TFieldValues>}
      render={({ field: { value, onChange } }) => (
        <TimePickerField
          value={(value as Date) ?? null}
          onChange={onChange}
          label={label}
          error={error ? error.toString() : undefined}
          placeholder={placeholder}
          disabled={disabled}
          size={size}
        />
      )}
    />
  );
};

export default TimePicker;
